// The #4314 conflict archive: keeping every losing branch body before it is closed.
//
// Extracted from DatabaseWrapper so it can be unit-tested without pulling in
// pouchdb-browser, which references `self` and fails to load under node. It was
// split out of database-wrapper-passthrough.ts, which had become the shared home
// for every testable sync helper regardless of subject (deferred finding
// df-08d4a072f17b).

import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { describeErrorForLog } from '@cf-shared/error-describe';

// ---------------------------------------------------------------------------
// #4314 Phase C — the conflict archive.
//
// Deleting a losing conflict branch is irreversible and destroys exactly the
// data a better merge algorithm would later need, so every losing body is
// copied somewhere local BEFORE its branch is closed.
//
// Storage is PouchDB `_local/` documents, one per archived revision, plus a
// small index. `_local/` was chosen over the device-local-id list because that
// list is matched by exact id, which would force a single ever-growing archive
// document — rewritten in full on every resolved document, and (worse) sorting
// inside the daily scan's key range so every scan would load the whole
// multi-megabyte body into memory. `_local/` documents never replicate and live
// in a separate table, so they never appear in `allDocs` and the scan never
// sees them. The custom iOS SQLite adapter implements the `_local` hooks, so
// this works on native too.
//
// The index is REQUIRED, not a convenience: `_local/` documents cannot be
// enumerated, so without it nothing could ever find the entries to evict them.
// It holds metadata only, so rewriting it stays cheap.
//
// See design-docs/sync-integrity.md ("Phase C — On-Device Conflict Auto-Resolve").
// ---------------------------------------------------------------------------

/** The id of the archive index. Metadata only — never the bodies themselves. */
export const CONFLICT_ARCHIVE_INDEX_ID = '_local/conflict-archive-index';

/** How long an archived losing body is kept. Comfortably longer than the time in
 *  which a bad merge would surface as a support ticket. */
export const CONFLICT_ARCHIVE_RETENTION_DAYS = 30;

/** Absolute ceiling on the archive, evicting oldest-first. Age alone would leave a
 *  pathological account unbounded on device storage. */
export const CONFLICT_ARCHIVE_MAX_BYTES = 5 * 1024 * 1024;

/** One row of the archive index. */
export interface ConflictArchiveIndexEntry {
  /** The `_local/` id of the archived entry. */
  key: string;
  /** When it was archived, ISO UTC. */
  archivedUtc: string;
  /** Roughly how big the archived body is, used only for the size backstop. */
  bytes: number;
  /**
   * What the #6847 restore step concluded about this copy, once it has. Absent on every row
   * written before #6847 and on every row archived since the last judgement (re-archiving the
   * same revision replaces the row), and absent means "not judged yet".
   */
  restoreOutcome?: string;
  /** When the restore step judged the row, ISO UTC. */
  restoreJudgedUtc?: string;
  /**
   * When the restore step first found a leaf of this document it could not read. A second
   * sighting judges the row, so a revision whose content is gone for good (#6415) cannot hold the
   * row back for ever.
   */
  restoreUnreadableSinceUtc?: string;
}

/** Has the #6847 restore step judged this archived copy? Until it has, the age rule keeps it. */
export function isRestoreJudged(entry: Partial<ConflictArchiveIndexEntry> | undefined): boolean {
  return typeof entry?.restoreOutcome === 'string' && entry.restoreOutcome.length > 0;
}

/** What one archive sweep evicted (#4314 R3/R7). */
export interface ConflictArchiveSweepResult {
  /**
   * How many archive operations failed during this sweep. The archive is what
   * makes the deletes defensible, so an archive that has quietly stopped
   * evicting — or stopped being readable at all — is exactly the thing nobody
   * would notice. A console line alone would be invisible in production, so the
   * count travels back to the caller and reaches telemetry.
   */
  archiveFailureCount: number;
  /**
   * The 5 MB ceiling fired at all. EXPECTED during the initial backlog drain —
   * an account clearing hundreds of documents at once can plausibly pass 5 MB in
   * a single day, and that is the mechanism working, not a fault.
   */
  archiveBackstopEvictedCount: number;
  /**
   * An entry YOUNGER than the retention window was dropped. This is the one that
   * actually means the sizing is wrong: a body was discarded while it was still
   * supposed to be recoverable, which quietly voids the recoverability promise
   * the ramp leans on. Kept separate from the counter above for exactly that
   * reason — during one long drain, oldest-first eviction can delete an archive
   * written minutes earlier in the same drain.
   */
  archiveEvictedWithinRetentionCount: number;
}

/** The database calls the archive needs, injected so it is testable under node. */
export interface ConflictArchiveDeps {
  /** Read a `_local/` document. Rejects with a not-found error when absent. */
  getLocal: (id: string) => Promise<any>;
  /** Write a `_local/` document. */
  putLocal: (doc: any) => Promise<any>;
  /** Delete a `_local/` document. A not-found here is tolerated by the sweep. */
  removeLocal: (id: string) => Promise<any>;
  logError: (message: string) => void;
}

/** The `_local/` id an archived revision is stored under. */
export function conflictArchiveEntryId(docId: string, rev: string): string {
  return `_local/conflict-archive/${docId}/${rev}`;
}

/** Roughly how many bytes a body occupies. Approximate on purpose — this feeds a
 *  storage budget, not an accounting ledger, so a character count is enough. */
function approximateBodyBytes(body: any): number {
  try {
    return JSON.stringify(body)?.length ?? 0;
  } catch {
    // a body that cannot be serialized cannot be sized; charge it nothing rather
    // than failing the archive, since the entry write below would fail anyway
    return 0;
  }
}

/** Read the archive index, treating an absent or malformed one as empty. */
async function readConflictArchiveIndexAsync(deps: ConflictArchiveDeps): Promise<any> {
  try {
    const doc = await deps.getLocal(CONFLICT_ARCHIVE_INDEX_ID);
    // a malformed index must not wedge the archive: fall back to empty and let
    // the next write rebuild it. The cost is that older entries stop being
    // evictable, which the size backstop still bounds.
    if (!doc || !Array.isArray(doc.entries)) {
      return { ...(doc || {}), _id: CONFLICT_ARCHIVE_INDEX_ID, entries: [] };
    }
    return doc;
  } catch (err) {
    if (!isPouchNotFound(err)) throw err;
    return { _id: CONFLICT_ARCHIVE_INDEX_ID, entries: [] };
  }
}

/**
 * Archive the losing bodies of one document before their branches are closed
 * (#4314 R3).
 *
 * Throws if anything fails. That is the contract the caller depends on: a failed
 * archive must abort the document with NO deletes, so merge-and-preserve
 * strictly precedes destroy.
 *
 * Write ordering is deliberate: the INDEX row goes first, then the entry body.
 * Taking the other order would leak — an entry written whose index update then
 * failed could never be found again, because `_local/` documents cannot be
 * enumerated, so nothing would ever evict it. This way a partial write leaves at
 * worst an index row pointing at a body that is not there, which the sweep
 * tolerates and clears, and which the next run overwrites anyway since the key is
 * derived from the revision.
 */
export async function archiveLosingRevisionsAsync(
  deps: ConflictArchiveDeps,
  docId: string,
  losingBodiesByRev: { rev: string, body: any }[],
  decisionLabel: string,
  nowUtcIso: string,
): Promise<void> {
  if (losingBodiesByRev.length === 0) return;

  const index = await readConflictArchiveIndexAsync(deps);
  const entries: ConflictArchiveIndexEntry[] = index.entries;

  // add every index row first, replacing any row for the same key
  for (const { rev, body } of losingBodiesByRev) {
    const key = conflictArchiveEntryId(docId, rev);
    const existing = entries.findIndex(entry => entry?.key === key);
    const row: ConflictArchiveIndexEntry = {
      key, archivedUtc: nowUtcIso, bytes: approximateBodyBytes(body),
    };
    if (existing >= 0) {
      entries[existing] = row;
    } else {
      entries.push(row);
    }
  }
  await deps.putLocal({ ...index, entries });

  // then the bodies themselves.
  //
  // Each write must carry the `_rev` of an entry that already exists. A `_local/`
  // put with no `_rev` onto an existing document is rejected with a 409 by BOTH
  // adapters, and re-archiving is not hypothetical — it happens on exactly the
  // paths this design treats as retryable. A merged put that 409s, or a delete
  // that fails for one of several losing revisions, aborts the document AFTER its
  // bodies were archived, so the next run archives the same revisions again.
  // Without the rev the retry would throw, and the document would be stuck for
  // good: never drained, and burning a failure report on every run for ever.
  for (const { rev, body } of losingBodiesByRev) {
    const entryId = conflictArchiveEntryId(docId, rev);
    let existingRev: string | undefined;
    try {
      existingRev = (await deps.getLocal(entryId))?._rev;
    } catch (err) {
      // absent is the normal first-archive case; anything else is a real problem
      if (!isPouchNotFound(err)) throw err;
    }
    const entry: any = {
      _id: entryId,
      docId,
      rev,
      decision: decisionLabel,
      archivedUtc: nowUtcIso,
      body,
    };
    if (existingRev) entry._rev = existingRev;
    await deps.putLocal(entry);
  }
}

/** The rows of the archive index, for the #6847 restore step. An absent or malformed index reads as none. */
export async function readConflictArchiveRowsAsync(deps: ConflictArchiveDeps): Promise<ConflictArchiveIndexEntry[]> {
  const index = await readConflictArchiveIndexAsync(deps);
  return index.entries.filter((entry: any) => typeof entry?.key === 'string');
}

/** One row's new restore fields, keyed by the row's `key`. */
export type ConflictArchiveRowMarks = Map<string, Pick<ConflictArchiveIndexEntry,
  'restoreOutcome' | 'restoreJudgedUtc' | 'restoreUnreadableSinceUtc'>>;

/**
 * Write what the #6847 restore step concluded onto the index rows, in one index write.
 *
 * Read fresh rather than taking the caller's copy, so a row archived since the caller read the
 * index is not lost. A mark for a row that has since been re-archived lands on the new row, which
 * is harmless: re-archiving happens only inside a resolver run, which this step never overlaps.
 */
export async function markConflictArchiveRowsAsync(deps: ConflictArchiveDeps, marks: ConflictArchiveRowMarks): Promise<void> {
  if (marks.size === 0) return;
  const index = await readConflictArchiveIndexAsync(deps);
  const entries = index.entries.map((entry: any) => {
    const mark = marks.get(entry?.key);
    return mark ? { ...entry, ...mark } : entry;
  });
  await deps.putLocal({ ...index, entries });
}

/**
 * Evict archived bodies that are past retention, then — if the archive is still
 * over the size ceiling — evict oldest-first until it fits (#4314 R3).
 *
 * Never throws: a failed sweep must not abort a drain that has already done its
 * real work. A failure to remove one entry leaves its index row in place, so the
 * next sweep retries it.
 */
export async function sweepConflictArchiveAsync(
  deps: ConflictArchiveDeps,
  nowMs: number,
): Promise<ConflictArchiveSweepResult> {
  const result: ConflictArchiveSweepResult = {
    archiveFailureCount: 0,
    archiveBackstopEvictedCount: 0,
    archiveEvictedWithinRetentionCount: 0,
  };

  let index: any;
  try {
    index = await readConflictArchiveIndexAsync(deps);
  } catch (err) {
    result.archiveFailureCount++;
    deps.logError(`conflict archive: could not read the index — ${describeErrorForLog(err)}`);
    return result;
  }

  const entries: ConflictArchiveIndexEntry[] = index.entries;
  if (entries.length === 0) return result;

  const retentionMs = CONFLICT_ARCHIVE_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const ageMs = (entry: ConflictArchiveIndexEntry) => nowMs - Date.parse(entry?.archivedUtc);
  const isWithinRetention = (entry: ConflictArchiveIndexEntry) => {
    const age = ageMs(entry);
    // an unparseable timestamp is treated as OLD, so a corrupt row can be
    // cleaned up rather than pinned in the archive for ever
    return Number.isFinite(age) && age < retentionMs;
  };

  // oldest first, so both eviction passes below walk in the right order. an
  // unparseable timestamp sorts oldest, which matches treating it as expired.
  const sorted = [...entries].sort((a, b) => {
    const aMs = Date.parse(a?.archivedUtc);
    const bMs = Date.parse(b?.archivedUtc);
    return (Number.isFinite(aMs) ? aMs : 0) - (Number.isFinite(bMs) ? bMs : 0);
  });

  const keep: ConflictArchiveIndexEntry[] = [];
  const evict: ConflictArchiveIndexEntry[] = [];

  // pass 1 — anything past its retention window goes, regardless of size, unless the #6847
  // restore step has not judged it yet. Such a row may hold the only copy left of a user's
  // entry, so it waits for the restore step however old it is. The hold is per row, so one
  // document the app always holds cannot pin every other row.
  for (const entry of sorted) {
    if (isWithinRetention(entry) || !isRestoreJudged(entry)) {
      keep.push(entry);
    } else {
      evict.push(entry);
    }
  }

  // pass 2 — if what is left still exceeds the ceiling, drop oldest-first until
  // it fits, held rows included: the ceiling bounds device storage and is never
  // waived. A dropped row still inside its retention window is counted
  // separately: that is the case that means the sizing is wrong.
  let keptBytes = keep.reduce((sum, entry) => sum + (Number(entry?.bytes) || 0), 0);
  while (keptBytes > CONFLICT_ARCHIVE_MAX_BYTES && keep.length > 0) {
    const oldest = keep.shift() as ConflictArchiveIndexEntry;
    keptBytes -= Number(oldest?.bytes) || 0;
    evict.push(oldest);
    result.archiveBackstopEvictedCount++;
    if (isWithinRetention(oldest)) result.archiveEvictedWithinRetentionCount++;
  }

  if (evict.length === 0) return result;

  // remove the bodies. a missing body is fine — it means a previous run wrote
  // the index row and then failed before the body landed, which is exactly the
  // partial-write state the write ordering is designed to leave behind.
  const removedKeys = new Set<string>();
  for (const entry of evict) {
    try {
      await deps.removeLocal(entry.key);
      removedKeys.add(entry.key);
    } catch (err) {
      if (isPouchNotFound(err)) {
        removedKeys.add(entry.key);
        continue;
      }
      // leave the row in place so the next sweep retries this entry
      result.archiveFailureCount++;
      deps.logError(`conflict archive: could not evict ${entry.key} — ${describeErrorForLog(err)}`);
    }
  }

  try {
    await deps.putLocal({ ...index, entries: entries.filter(entry => !removedKeys.has(entry?.key)) });
  } catch (err) {
    result.archiveFailureCount++;
    deps.logError(`conflict archive: could not rewrite the index after eviction — ${describeErrorForLog(err)}`);
  }

  return result;
}
